import { JobStatus } from '@funnellab/shared/src/types/directus-enums.ts';
import { type LoaderFunction, redirect } from 'react-router-dom';
import { NEW_JOB_ID, Paths } from '@src/paths.ts';
import { SearchParamKeys } from '@funnellab/shared/src/types/SearchParamKeys.ts';
import { getSteps } from '@src/components/jobWizard/steps';
import employerBrandService from '@src/api/services/EmployerBrandService.ts';
import jobService from '@src/api/services/JobService.ts';
import organizationService from '@src/api/services/OrganizationService.ts';
import settingsService from '@src/api/services/SettingsService.ts';
import type { EmployerBrandDto } from '@src/api/api.types.ts';
import type { JobForWizard } from '@src/api/queries/job';
import type { OrganizationDto } from '@src/api/queries/organization';
import type { Settings } from '@funnellab/shared/src/types/directus.ts';

export type CreateJobWizardViewLoaderData = {
  job: JobForWizard | EmptyJob;
  organizations: OrganizationDto[];
  employerBrands: EmployerBrandDto[];
  settings: Settings;
};

export type EmptyJob = {
  status: JobStatus.DRAFT;
  organization: string | null;
};

export const editJobWizardViewLoader = (async ({
  params,
  request,
}): Promise<CreateJobWizardViewLoaderData | Response> => {
  const jobId = params.jobId;
  if (!jobId) {
    return redirect(Paths.ROOT);
  }

  const isNewJob = jobId === NEW_JOB_ID;

  const [organizations, employerBrands, settings] = await Promise.all([
    organizationService.getAllOrganizations(),
    employerBrandService.getAllEmployerBrands(),
    settingsService.getSettings(),
  ]);
  const url = new URL(request.url);
  const orgId = url.searchParams.get(SearchParamKeys.ORG_ID);

  if (isNewJob && organizations.length === 0) {
    return redirect(Paths.ORGANIZATIONS);
  }

  const job = isNewJob
    ? createJob(orgId)
    : await jobService.getJobForWizard(jobId);

  if (job.status !== JobStatus.DRAFT && job.status !== JobStatus.REACTIVATING) {
    // Jobs can only be edited while they are in draft or reactivation status
    return redirect(Paths.ROOT);
  }

  if (!url.searchParams.has('step')) {
    const paymentByInvoice = await getPaymentByInvoice(job.organization);
    const steps = await getSteps({
      searchParams: url.searchParams,
      paymentByInvoice,
      jobStatus: job.status,
    });
    // Automatically redirect to the first page with incomplete data
    const firstUnfinishedStepIndex = steps.findIndex(
      // TODO this could be an empty job. But I don't know how to fix all of this anymore...
      (step) => !step.isValid(job as JobForWizard),
    );
    if (firstUnfinishedStepIndex === -1) {
      // All steps are complete
      url.searchParams.set('step', `${steps.length}`);
    } else {
      url.searchParams.set('step', `${firstUnfinishedStepIndex + 1}`);
    }
    return redirect(url.toString());
  }

  return {
    job,
    organizations,
    employerBrands,
    settings,
  };
}) satisfies LoaderFunction;

const getPaymentByInvoice = async (
  org: string | { paymentByInvoice: boolean } | null | undefined,
) => {
  if (!org) {
    return false;
  }
  const organization =
    typeof org === 'string'
      ? await organizationService.getOrganizationById(org)
      : org;
  return organization.paymentByInvoice;
};

const createJob = (orgId: string | null): EmptyJob => ({
  status: JobStatus.DRAFT,
  organization: orgId ?? null,
});
